export interface DraftGroup {
  name: string;
  competitionIds: number[];
  minStartTime: string;
  maxStartTime: string;
  contestsStartTerm: string;
  startTimeSuffix: string;
  startTimeType: string;
  draftGroupState: string;
}

export interface IOptimizerState {
  additionalLegs: {
    outcomes: IShopOutcome[];
    pagination: Partial<IShopOutcomesPagination>;
  };
  matchups: IMatchupsResponse[];
  matchupsFilter: IMatchupsResponse[];
  offersFilter: string[];
  entries: OptimizerEntry[];
  optimizerData: OptimizerData[];
  selectedDraftGroup: DraftGroup;
  selectedEntry: number;
  isEntryView: boolean;
  maxLegs: number;
}

export interface OptimizerEntry {
  id: number;
  outcomes: IShopOutcome[];
}

export interface OptimizerData {
  leagueCode: string;
  draftGroups: DraftGroup[];
  payouts: Payout[];
}

export interface Payout {
  pickSetSize: number;
  numberOfPicksCorrect: number;
  lowPayoutMultiplier: number;
  averagePayoutMultiplier: number;
  highPayoutMultiplier: number;
}

export interface IShopOutcome extends IBaseOutcome, IProjectionProps, IDataGridCellUpdates {
  holdPct?: string | null;
  holdValue?: number;
  additionalOdds: IShopAdditonalOutcome[];
  readonly awayTeamCityName: string;
  readonly homeTeamSchool: string;
  readonly createdDate: string;
  readonly homeTeamCityName: string;
  readonly sportsbookEventId: string;
  readonly matchupOpponents: {
    displayName: string;
    firstName: string;
    id: string;
    lastName: string;
  }[];
  trueOdds: string | null | undefined;
  created: Date | null;
  oddsDecimal: number;
  currentLine: string | null;
  unitsRisked: number | null;
  readonly outcomeId: string;
  readonly parlayOutcomes?: IShopOutcome[];
  readonly id: string;
  readonly sportsbookOfferValue: string;
  readonly sportsbookOfferCode: string;
  readonly sportsbookOfferUrl: string;
  readonly evAge: Date | null;
  notes?: string | null;
  unitSize?: number | null;
  freePick?: boolean;
  parlayLegs?: IShopOutcome[];
  currentOdds?: string;
  userBetType?: string;
  readonly userId?: string;
  openBets?: IShopOutcome[];
  readonly betAmount?: number;
  readonly betDisplayLabel?: string;
  readonly currentOddsAmerican?: string;
  readonly isBestBet?: boolean;
  readonly customBet: boolean;
  // SGP stuff
  correlationScore?: number;
  correlatedSimTrueWinProbability?: number;
  simTrueWinProbability?: number;
  correlatedSharpTrueWinProbability?: number | null;
  simSEV?: number;
  simSBet?: number;
  simExpectedRoi?: number;
}



export interface IBaseOutcome {
  readonly id: string;
  readonly outcomeId: string;
  readonly oddsDecimal: number;
  oddsAmerican: string | number | null;
  sportsbookLogo: string;
  readonly awayTeamAbbreviation: string | null;
  readonly awayTeamId: string | null;
  readonly awayTeamLogo: string | null;
  readonly awayTeamName: string | null;
  readonly awayTeamTitle: string | null;
  readonly betType: string;
  readonly conferenceId: string | null;
  readonly conferenceParticipantLogo: string | null;
  readonly deepLinkUrl: string;
  readonly divisionId: string | null;
  readonly divisionParticipantLogo: string | null;
  readonly eventId: string;
  readonly eventName: string;
  readonly eventStartDate: string;
  readonly eventLabel: string;
  readonly hashCode: string;
  readonly hashCodeBetSideNoLine: string;
  readonly hashCodeBetSideWithLine: string;
  readonly homeTeamAbbreviation: string | null;
  readonly homeTeamId: string | null;
  readonly homeTeamLogo: string | null;
  readonly homeTeamName: string | null;
  readonly homeTeamTitle: string | null;
  readonly injuryStatus?: string;
  readonly label: string;
  readonly leagueCode: string;
  readonly line: string | null;
  readonly offerCategory: string;
  readonly offerId: string;
  readonly offerName: string;
  readonly participantCity: string;
  readonly participantLabel: string;
  readonly participantAbbreviation: string | null;
  readonly participantDataProviderId: number | null;
  readonly participantName: string;
  readonly participantTitle: string | null;
  readonly participantType: string;
  readonly participantUrl: string;
  readonly playerId: string;
  readonly playerParticipantHeadShot: string;
  readonly playerTeamAbbreviation: string;
  readonly playerTeamId: string;
  readonly playerTeamName: string;
  readonly playerTeamTitle: string;
  readonly sportsbookCode: string;
  readonly teamId: string | null;
  readonly teamParticipantLogo: string | null;
  readonly isPromo: boolean;
}

export interface IProjectionProps {
  awesemoProj?: string | null;
  expectedRoi?: number;
  expectedRoiPct?: string | null;
  sev?: number;
  sevPct?: string | null;
  expectedWin: number;
  expectedWinPct: string | null;
  suggestedBet: number;
  suggestedBetPct: string | null;
}

export interface IDataGridCellUpdates {
  oddsUpdatedPositive?: boolean | null;
  evUpdatedPositive?: boolean | null;
  xWinUpdatedPositive?: boolean | null;
  sBetUpdatedPositive?: boolean | null;
  sevUpdatedPositive?: boolean | null
}


export interface IShopOutcomesPagination {
  readonly totalCount: number;
  readonly pageSize: number;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly previousPageLink: string;
  readonly nextPageLink: string;
}

export interface IMatchupsResponse {
  readonly id: string;
  readonly dataProviderId: number;
  readonly leagueInstanceId: string;
  readonly eventType: string;
  readonly name: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly leagueInstance: ILeagueInstance;
  readonly participants: IMatchupsParticipants[];
  readonly label: string;
}

export interface ILeagueInstance {
  readonly id: string;
  readonly sportCode: string;
  readonly leagueCode: string;
  readonly name: string;
  readonly displayName: string;
  readonly startDate: string;
  readonly endDate: string;
}

export interface IMatchupsParticipants {
  readonly eventId: string;
  readonly id: string;
  readonly teamId: string;
  readonly isHome: boolean;
  readonly team: ITeam;
}


export interface IShopAdditonalOutcome {
  readonly label: string;
  readonly line: string | null;
  readonly oddsAmerican: string | number | null;
  readonly americanOdds?: string;
  readonly oddsDecimal: number;
  readonly sportsbookCode: string;
  readonly deepLinkUrl: string;
  readonly hashCode: string;
  sportsbookLogo?: string;
  readonly sportsbookOfferCode: string;
  readonly sportsbookOfferValue: string;
  readonly sportsbookOfferUrl: string;
  readonly created: Date;
}

export interface ITeam {
  readonly id: string;
  readonly dataProviderId: number;
  readonly conferenceId: string;
  readonly divisionId: string;
  readonly displayName: string;
  readonly queryName: string;
  readonly title: string;
  readonly abbreviation: string;
  readonly city: string;
  readonly school: string;
  readonly sportCode: string;
  readonly logo: string;
  readonly leagueCode: string;
  readonly highResLogo: string;
  readonly lowResLogo: string;
}

export interface IToolFilters {
  league: Leagues;
  timeframe: string;
  state?: IState;
  offers: IShopOffer[];
}
export enum Leagues {
  NBA = 'NBA',
  NFL = 'NFL',
  MLB = 'MLB',
  NHL = 'NHL',
  WNBA = 'WNBA',
  GOLF = 'Golf',
  MMA = 'MMA',
  UFC = 'UFC',
  BL = 'Bundesliga',
  EPL = 'EPL',
  TENNIS = 'Tennis',
  CL = 'CL',
  NCAAB = 'NCAAB',
  NCAAF = 'NCAAF',
  UNKNOWN = 'UNKNOWN',
  ALL = 'ALL'
}

export interface IState {
  readonly id: string;
  readonly name: string;
  readonly abbreviation: string;
  readonly sportsbooks?: ISportsbook[];
  isLegalized: boolean;
}


export interface ISportsbook {
  id: string;
  sportsbookCode: string;
  logoUrl?: string;
  operatorType: string;
  name?: string;
  isActive?: boolean;
}

export interface IShopOffer {
  readonly id: string;
  readonly name: string;
  readonly sportCode: string;
  readonly leagueCode: string;
  readonly offerCategory: string;
  readonly eventType: string;
  readonly betType: string;
  readonly participantType: string;
  readonly labelType: string;
  uri?: string;
}

export enum Pick6MaxLegs {
  MLB_SINGLE_GAME_SLATE = 4,
  DEFAULT = 6,
}

export interface IParlayOutcome extends IShopOutcome {
  readonly projection: string;
}

export enum Operators {
  PRIZEPICKS = 'PrizePicks',
  UNDERDOG = 'Underdog',
  PICK6 = 'Pick6',
  NONE = 'NONE'
}

export enum PickemParlayType {
  FLEX = 'Flex',
  POWER = 'Power',
}

export enum Timeframe {
  // Current Timeframes
  // These operate as they would on a calendar.
  // Example: Today is Wednesday. WEEK starts on Sunday and ends on Saturday
  TODAY = 'today',
  TOMORROW = 'tomorrow',
  YESTERDAY = 'yesterday',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
  ALL = 'all',
  // Past Timeframes
  // These operate as an "up to date" window. They start as they would on a calendar but stop on the current day.
  // Example: Today is a Wednesday. PAST_WEEK Starts on Sunday and ends on Wednesday.
  PAST_WEEK = 'pastWeek',
  PAST_MONTH = 'pastMonth',
  PAST_YEAR = 'pastYear',
  PAST_ALL = 'pastAll',
  // Future Timeframes
  // These operate as an "from here on" window. These start on the current day and end as they would on a calendar.
  // Example: Today is Wednesday. FUTURE_WEEK starts on Wednesday and ends on Saturday.
  FUTURE_WEEK = 'futureWeek',
  FUTURE_MONTH = 'futureMonth',
  FUTURE_YEAR = 'futureYear',
  FUTURE_ALL = 'futureAll',
  // Past Window Timeframes
  // These ignore calendar structures and operate on a fixed number of days. These start on the specified interval and end on the current day.
  // Example: Today is a Wednesday. PAST_7_DAYS starts on the previous Wednesday and ends on Wednesday.
  PAST_7_DAYS = 'past7',
  PAST_30_DAYS = 'past30',
  PAST_365_DAYS = 'past365',
  // Future Window Timeframes
  // These ignore calendar structures and operate on a fixed number of days. These start on the current day and end on the specified interval.
  // Example: Today is a Wednesday. FUTURE_7_DAYS starts on Wednesday and ends on the next Wednesday.
  FUTURE_7_DAYS = 'future7',
  FUTURE_30_DAYS = 'future30',
  FUTURE_365_DAYS = 'future365',
  //Other
  SEASON_LONG = 'season',
  PREVIOUS_SEASON = 'previousSeason',
}

export enum EventTypes {
  Game = 'Game',
  Season = 'Season',
  Tournament = 'Tournament'
}


export const shopOddStates = [
  {
    id: '918cb893-fa84-4103-93f4-1acdec9e8333',
    name: 'Alabama',
    abbreviation: 'AL',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '1fcca1c8-8c15-4317-a583-ec74f49283b3',
    name: 'Alaska',
    abbreviation: 'AK',
    isLegalized: true,
    sportsbooks: [
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: 'cc72d987-b7dc-4da4-8f43-068237ee9b52',
    name: 'Alberta (CA)',
    abbreviation: 'AB',
    isLegalized: true,
    sportsbooks: [
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '0ed47684-d7c3-4192-9ba8-5721ec033ed7',
    name: 'Alberta (CA)',
    abbreviation: 'AB',
    isLegalized: true,
    sportsbooks: [
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '73fda334-12b6-47b8-8e8b-42bc74ef9736',
    name: 'Arizona',
    abbreviation: 'AZ',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'bc49c8df-3bbe-44a6-8979-4a64fd82a43d',
        sportsbookCode: 'HardRock',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/HardRockLogo.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '0c5bcfa1-e7ba-4119-a6c4-15f9d704c772',
        sportsbookCode: 'Unibet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/unibet-logo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '61cf9fdc-a30d-4d00-8875-5031d71f5716',
    name: 'Arkansas',
    abbreviation: 'AR',
    isLegalized: true,
    sportsbooks: [
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: 'bbf3e76e-ce29-4d25-a60e-f7d0f15f353a',
    name: 'British Columbia (CA)',
    abbreviation: 'BC',
    isLegalized: true,
    sportsbooks: [
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '69d72f39-2a18-4c16-b20e-a96390351dd3',
    name: 'California',
    abbreviation: 'CA',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '299ba5cc-c067-4b03-8b6f-ad8fd4d38e08',
    name: 'Colorado',
    abbreviation: 'CO',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f6372add-d7cc-42f3-b532-fe0e3f7edd57',
        sportsbookCode: 'Circa',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/CircaLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'e7797af1-ad3a-4872-80d1-3afcc5a666c6',
        sportsbookCode: 'Tipico',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/tipico-logo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '0fddb650-bd24-4697-905a-245c181a1e0c',
    name: 'Connecticut',
    abbreviation: 'CT',
    isLegalized: true,
    sportsbooks: [
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '244124e2-0c7b-4956-bd92-6e4e09765c84',
    name: 'Delaware',
    abbreviation: 'DE',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '8d1d23fb-aa1d-4a20-93d7-25d5fc271c37',
    name: 'Florida',
    abbreviation: 'FL',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'bc49c8df-3bbe-44a6-8979-4a64fd82a43d',
        sportsbookCode: 'HardRock',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/HardRockLogo.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '942c20da-c6f0-48fd-b104-006b27a6e7ca',
    name: 'Georgia',
    abbreviation: 'GA',
    isLegalized: true,
    sportsbooks: [
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '115b1b1e-3875-4d6a-a842-b29456e821de',
    name: 'Illinois',
    abbreviation: 'IL',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'f6372add-d7cc-42f3-b532-fe0e3f7edd57',
        sportsbookCode: 'Circa',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/CircaLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: 'ab8981de-2698-4a1b-ac04-c24d87aa43e7',
    name: 'Indiana',
    abbreviation: 'IN',
    isLegalized: true,
    sportsbooks: [
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '0c5bcfa1-e7ba-4119-a6c4-15f9d704c772',
        sportsbookCode: 'Unibet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/unibet-logo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'bc49c8df-3bbe-44a6-8979-4a64fd82a43d',
        sportsbookCode: 'HardRock',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/HardRockLogo.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: 'f7294196-3238-40d9-9f57-42ae8719ae35',
    name: 'Iowa',
    abbreviation: 'IA',
    isLegalized: true,
    sportsbooks: [
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'e7797af1-ad3a-4872-80d1-3afcc5a666c6',
        sportsbookCode: 'Tipico',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/tipico-logo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f6372add-d7cc-42f3-b532-fe0e3f7edd57',
        sportsbookCode: 'Circa',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/CircaLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '0457b934-c956-41cf-875b-eba7394bac80',
    name: 'Kansas',
    abbreviation: 'KS',
    isLegalized: true,
    sportsbooks: [
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: 'f99f02f7-02af-4159-8454-8f1e5b7d6a8d',
    name: 'Kentucky',
    abbreviation: 'KY',
    isLegalized: true,
    sportsbooks: [
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '805d0640-ed1b-406c-ad87-04261e23ef5e',
    name: 'Louisiana',
    abbreviation: 'LA',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '846a9ecc-2256-40c6-b50e-9a5ca9fbce71',
    name: 'Maine',
    abbreviation: 'ME',
    isLegalized: true,
    sportsbooks: [
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '576a30b1-a06c-4f6c-909b-173ffd09cf2a',
    name: 'Manitoba (CA)',
    abbreviation: 'MB',
    isLegalized: true,
    sportsbooks: [
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: 'da0c06e2-8c5e-413b-8463-38de5c6f4c9b',
    name: 'Maryland',
    abbreviation: 'MD',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '81d7d781-63a1-4138-a5c5-3340e7d2ec42',
    name: 'Massachusetts',
    abbreviation: 'MA',
    isLegalized: true,
    sportsbooks: [
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: 'ebd4f26b-00ef-4e40-ac2c-c80268fd622c',
    name: 'Michigan',
    abbreviation: 'MI',
    isLegalized: true,
    sportsbooks: [
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: 'e7bcd1e8-a3ed-47b6-af6c-952b199798d9',
    name: 'Minnesota',
    abbreviation: 'MN',
    isLegalized: true,
    sportsbooks: [
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '88414e89-d1b4-40ef-af59-20a4ba576095',
    name: 'Nebraska',
    abbreviation: 'NE',
    isLegalized: true,
    sportsbooks: [
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '4f4b1821-1197-4795-be93-0b1fb5e08ae9',
    name: 'Nevada',
    abbreviation: 'NV',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'f6372add-d7cc-42f3-b532-fe0e3f7edd57',
        sportsbookCode: 'Circa',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/CircaLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: 'e38b6720-718c-40f0-b40e-169abc1293df',
    name: 'New Brunswick (CA)',
    abbreviation: 'NB',
    isLegalized: true,
    sportsbooks: [
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: 'f88a5439-4c5a-44a3-b25d-1cc8e8b8b77f',
    name: 'New Hampshire',
    abbreviation: 'NH',
    isLegalized: true,
    sportsbooks: [
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: 'a649a8f6-4f82-4505-b6fb-5b12712fa48f',
    name: 'New Jersey',
    abbreviation: 'NJ',
    isLegalized: true,
    sportsbooks: [
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'df52ac01-09b4-4185-ad43-c333f2119d06',
        sportsbookCode: 'SugarHouse',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Sugarhouse/SVG/Sugar_House_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'e7797af1-ad3a-4872-80d1-3afcc5a666c6',
        sportsbookCode: 'Tipico',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/tipico-logo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '0c5bcfa1-e7ba-4119-a6c4-15f9d704c772',
        sportsbookCode: 'Unibet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/unibet-logo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'bc49c8df-3bbe-44a6-8979-4a64fd82a43d',
        sportsbookCode: 'HardRock',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/HardRockLogo.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '39917afc-dc5a-44a3-b6b5-8f4480ea9104',
    name: 'New Mexico',
    abbreviation: 'NM',
    isLegalized: true,
    sportsbooks: [
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '0aa16e91-b190-4046-881f-92b85d6b1ba5',
    name: 'New York',
    abbreviation: 'NY',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '78ae2174-7b16-429f-a846-e454f5baa331',
    name: 'Newfoundland and Labrador (CA)',
    abbreviation: 'NL',
    isLegalized: true,
    sportsbooks: [
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: 'fabaf2f7-7aec-499e-86c9-8b72dbafbc3c',
    name: 'North Carolina',
    abbreviation: 'NC',
    isLegalized: true,
    sportsbooks: [
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '6bda75c7-218f-4c5a-8731-d1c0fdf5162e',
    name: 'North Dakota',
    abbreviation: 'ND',
    isLegalized: true,
    sportsbooks: [
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '82eb26ba-c953-466c-af70-4f40077fdd42',
    name: 'Northwest Territories (CA)',
    abbreviation: 'NT',
    isLegalized: true,
    sportsbooks: [
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '8b28a8c9-7558-430d-bfb9-da8b2d6c6100',
    name: 'Nova Scotia (CA)',
    abbreviation: 'NS',
    isLegalized: true,
    sportsbooks: [
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: 'a0c30290-a1e5-4a08-b3e2-52b6a3d5b423',
    name: 'Nunavut (CA)',
    abbreviation: 'NU',
    isLegalized: true,
    sportsbooks: [
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '6e11ec31-50a9-4f7d-8c84-de4f62bc6211',
    name: 'Ohio',
    abbreviation: 'OH',
    isLegalized: true,
    sportsbooks: [
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'e7797af1-ad3a-4872-80d1-3afcc5a666c6',
        sportsbookCode: 'Tipico',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/tipico-logo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'bc49c8df-3bbe-44a6-8979-4a64fd82a43d',
        sportsbookCode: 'HardRock',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/HardRockLogo.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '72b7f0db-78ff-42a8-877b-1505fe1acc68',
    name: 'Oklahoma',
    abbreviation: 'OK',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: 'e41ab3f2-8e2c-4e32-802b-18349e3282fc',
    name: 'Ontario (CA)',
    abbreviation: 'ON',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '284786f0-ab98-4c24-8da6-b8d779bc9fb2',
        sportsbookCode: 'PointsBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/PointsBet/SVG/Points_Bet_Logo_Color_light_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: 'cfa2a4f2-36d7-4411-9448-401401039aaa',
    name: 'Oregon',
    abbreviation: 'OR',
    isLegalized: true,
    sportsbooks: [
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '7975373c-2103-4f47-a911-6c8d78d9a740',
    name: 'Pennsylvania',
    abbreviation: 'PA',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'df52ac01-09b4-4185-ad43-c333f2119d06',
        sportsbookCode: 'SugarHouse',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Sugarhouse/SVG/Sugar_House_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '0c5bcfa1-e7ba-4119-a6c4-15f9d704c772',
        sportsbookCode: 'Unibet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/unibet-logo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '696dadb1-2b6e-47d5-a65d-0ec382157a94',
    name: 'Prince Edward Island (CA)',
    abbreviation: 'PE',
    isLegalized: true,
    sportsbooks: [
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '97dcc3ab-c034-490f-9669-8b6a94ad2a24',
    name: 'Puerto Rico',
    abbreviation: 'PR',
    isLegalized: true,
    sportsbooks: [
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '2af214b1-9d66-470c-8ae4-1730351cde32',
    name: 'Quebec (CA)',
    abbreviation: 'QC',
    isLegalized: true,
    sportsbooks: [
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '377c58a4-dacb-4b7e-9203-0698a6a3b071',
    name: 'Rhode Island',
    abbreviation: 'RI',
    isLegalized: true,
    sportsbooks: [
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: 'cf3aea94-5b6e-4afd-86fb-4b5d13d5a84c',
    name: 'Saskatchewan (CA)',
    abbreviation: 'SK',
    isLegalized: true,
    sportsbooks: [
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '9667d124-9968-430b-987b-c674d421c2e9',
    name: 'South Carolina',
    abbreviation: 'SC',
    isLegalized: true,
    sportsbooks: [
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: 'c671d7e9-3f98-4dbc-b700-151c1dd13897',
    name: 'South Dakota',
    abbreviation: 'SD',
    isLegalized: true,
    sportsbooks: [
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: 'f47f9f16-c11d-4ef1-ad61-b91f57162e17',
    name: 'Tennessee',
    abbreviation: 'TN',
    isLegalized: true,
    sportsbooks: [
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'bc49c8df-3bbe-44a6-8979-4a64fd82a43d',
        sportsbookCode: 'HardRock',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/HardRockLogo.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '386c9b88-0aba-492c-8f6b-8319f2d94e1f',
    name: 'Texas',
    abbreviation: 'TX',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '2fa5dc0b-763c-4192-85bf-0031f254dd83',
    name: 'Utah',
    abbreviation: 'UT',
    isLegalized: true,
    sportsbooks: [
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: 'aaab14d0-86b8-428a-b145-64b2011123fc',
    name: 'Vermont',
    abbreviation: 'VT',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      }
    ]
  },
  {
    id: '60226826-5bb9-4627-a679-2ee8aa50b6f3',
    name: 'Virginia',
    abbreviation: 'VA',
    isLegalized: true,
    sportsbooks: [
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'bc49c8df-3bbe-44a6-8979-4a64fd82a43d',
        sportsbookCode: 'HardRock',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/HardRockLogo.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '0c5bcfa1-e7ba-4119-a6c4-15f9d704c772',
        sportsbookCode: 'Unibet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/unibet-logo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: 'f2a67545-4657-4604-8955-5d946373411f',
    name: 'Washington, D.C.',
    abbreviation: 'DC',
    isLegalized: true,
    sportsbooks: [
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '8d8a1aff-df2b-467e-8983-d6564bd7cd6f',
    name: 'West Virginia',
    abbreviation: 'WV',
    isLegalized: true,
    sportsbooks: [
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'f1cf48dc-c727-4479-ba5e-464555011949',
        sportsbookCode: 'ESPNBet',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/ESPNBetSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '6191303e-aed4-4a19-8d54-5fcae470e54f',
        sportsbookCode: 'Caesars',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/CZRs/SVG/CZRs_Logo_light_color.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '61186941-d592-4930-aecd-2ca66ed22e72',
        sportsbookCode: 'Fanatics',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FanaticsSmall.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'ee4163d0-c61c-411c-bdb2-b2093b368860',
        sportsbookCode: 'BetRivers',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Betrivers/SVG/Bet_Rivers_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '59266d14-9963-4645-8a5b-6555d1a67df5',
    name: 'Wisconsin',
    abbreviation: 'WI',
    isLegalized: true,
    sportsbooks: [
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: 'e066971b-57d6-4b69-8aea-a5b41a6340de',
        sportsbookCode: 'Dabble',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/DabbleLogo.png',
        operatorType: 'PickEm'
      },
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: 'd2adcab4-f59b-407d-86b9-b57e7db990c7',
        sportsbookCode: 'Sleeper',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/sleeper-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '9702a583-dd1f-4a54-9cfc-267d6bae0ff8',
        sportsbookCode: 'Pick6',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/Pick6Logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '3bb481e5-6af1-423b-a03b-5e092dc5d2b9',
    name: 'Wyoming',
    abbreviation: 'WY',
    isLegalized: true,
    sportsbooks: [
      {
        id: '5ccd9837-47c8-46a0-aa9d-d60c33dfe09f',
        sportsbookCode: 'DraftKings',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/DraftKings_Sportsbook/SVG/Draftkings_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '9d5c58d9-5269-46bd-9abc-d4b4baf93b36',
        sportsbookCode: 'BetMGM',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/All_SVG_Logos/Bet_MGM_Logo_Color_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: '7a702323-c69f-46db-b63f-6a1e1b8c73c8',
        sportsbookCode: 'Fliff',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/FliffLogo.png',
        operatorType: 'Sportsbook'
      },
      {
        id: 'acf69670-21d6-4edb-bb2a-957992d81b16',
        sportsbookCode: 'FanDuel',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/FanDuel_Sportsbook/SVG/Fanduel_Sportsbook_Logo_White_short.svg',
        operatorType: 'Sportsbook'
      },
      {
        id: 'c34da533-a19a-4ed1-9781-aba1408cfa79',
        sportsbookCode: 'Betr',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/betr-logo.png',
        operatorType: 'PickEm'
      }
    ]
  },
  {
    id: '58142877-ea7a-462f-af7d-800e4407cce1',
    name: 'Yukon (CA)',
    abbreviation: 'YT',
    isLegalized: true,
    sportsbooks: [
      {
        id: '727dc7fc-4176-479d-a3ba-a52260768a29',
        sportsbookCode: 'PrizePicks',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/PrizePicks_logo_288.png',
        operatorType: 'PickEm'
      },
      {
        id: '165f8e7f-e0af-4ddf-8866-f52b5abe5545',
        sportsbookCode: 'Underdog',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/assets/sportsbooks/underdog-logo.png',
        operatorType: 'PickEm'
      },
      {
        id: '8a4991f7-39c9-40ce-96bb-2430382b7320',
        sportsbookCode: 'Bet365',
        logoUrl: 'https://awesemostorage.blob.core.windows.net/affiliates/Bet365/PNG/bet365-square-sportsbook-logo-100x100.png',
        operatorType: 'Sportsbook'
      }
    ]
  }
]
